import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Copy, CheckCircle, AlertCircle, Info, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GeneratorPage = () => {
  const { language } = useLanguage();
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    
    const cleanInput = input.trim().replace(/\s/g, '').toUpperCase();
    
    if (!cleanInput) {
      setError(language === 'nl' ? 'Voer een aanslagnummer in' : 'Enter an assessment number');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/generate`, {
        aanslagnummer: cleanInput
      });
      
      if (response.data.success) {
        setResult(response.data);
      } else {
        setError(response.data.error || 'Genereren mislukt');
      }
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        (language === 'nl' ? 'Er ging iets mis. Controleer het aanslagnummer.' : 'Something went wrong. Check the assessment number.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (result?.betalingskenmerk) {
      await navigator.clipboard.writeText(result.betalingskenmerk);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const examples = [
    { aanslag: '036000012L0123110', type: 'Loonheffing' },
    { aanslag: '036000012B0223270', type: 'Omzetbelasting (BTW)' },
    { aanslag: '036000012H2000010', type: 'Inkomstenbelasting' },
  ];

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full text-accent text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            {language === 'nl' ? 'Nieuwe tool' : 'New tool'}
          </div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-primary dark:text-white mb-4">
            {language === 'nl' ? 'Betalingskenmerk Generator' : 'Payment Reference Generator'}
          </h1>
          <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
            {language === 'nl'
              ? 'Genereer een betalingskenmerk vanuit een aanslagnummer van de Belastingdienst'
              : 'Generate a payment reference from a Dutch tax assessment number'}
          </p>
        </motion.div>

        {/* Generator Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-8 mb-8"
        >
          <form onSubmit={handleGenerate} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {language === 'nl' ? 'Aanslagnummer' : 'Assessment Number'}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={language === 'nl' ? 'Bijv. 036000012L0123110' : 'E.g., 036000012L0123110'}
                  className="w-full px-4 py-4 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-lg font-mono tracking-wider focus:border-accent focus:ring-0 placeholder-slate-400"
                  data-testid="generator-input"
                />
              </div>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                {language === 'nl'
                  ? '9 cijfers + 1 letter + 6-7 cijfers (bijv. 036000012L0123110)'
                  : '9 digits + 1 letter + 6-7 digits (e.g., 036000012L0123110)'}
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 px-6 rounded-xl bg-accent text-white font-semibold text-lg hover:bg-accent-hover transition-colors disabled:opacity-50 flex items-center justify-center gap-3"
              data-testid="generator-submit"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <ArrowRight className="w-5 h-5" />
                  {language === 'nl' ? 'Genereer Betalingskenmerk' : 'Generate Payment Reference'}
                </>
              )}
            </button>
          </form>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mt-6 p-4 bg-error/10 border border-error/20 rounded-xl flex items-start gap-3"
              >
                <AlertCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
                <p className="text-error">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Result */}
          <AnimatePresence>
            {result && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mt-8"
              >
                <div className="bg-gradient-to-br from-accent/5 to-blue-500/5 border border-accent/20 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                      {language === 'nl' ? 'Gegenereerd betalingskenmerk' : 'Generated payment reference'}
                    </span>
                    <span className="flex items-center gap-1 text-success text-sm">
                      <CheckCircle className="w-4 h-4" />
                      {language === 'nl' ? 'Geldig' : 'Valid'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between gap-4">
                    <div className="text-3xl sm:text-4xl font-mono font-bold text-primary dark:text-white tracking-wider">
                      {result.formatted}
                    </div>
                    <button
                      onClick={handleCopy}
                      className={`p-3 rounded-xl transition-colors ${
                        copied 
                          ? 'bg-success text-white' 
                          : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
                      }`}
                      data-testid="copy-result"
                    >
                      {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    </button>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div>
                        <span className="text-slate-500 dark:text-slate-400">
                          {language === 'nl' ? 'Belastingtype:' : 'Tax type:'}
                        </span>{' '}
                        <span className="font-medium text-slate-900 dark:text-white">
                          {result.belastingtype}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Examples */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <Info className="w-5 h-5 text-accent" />
            <h3 className="font-semibold text-slate-900 dark:text-white">
              {language === 'nl' ? 'Voorbeelden' : 'Examples'}
            </h3>
          </div>
          <div className="space-y-3">
            {examples.map((example, idx) => (
              <button
                key={idx}
                onClick={() => setInput(example.aanslag)}
                className="w-full flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-left"
              >
                <span className="font-mono text-sm text-slate-900 dark:text-white">
                  {example.aanslag}
                </span>
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  {example.type}
                </span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Info Box */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl"
        >
          <p className="text-sm text-amber-800 dark:text-amber-200">
            {language === 'nl'
              ? 'Let op: Sommige aanslagnummers (zoals milieuheffingen) hebben een speciaal gegenereerd betalingskenmerk dat niet via deze tool berekend kan worden.'
              : 'Note: Some assessment numbers (like environmental taxes) have a specially generated payment reference that cannot be calculated via this tool.'}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default GeneratorPage;
